import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  IconButton,
  Collapse,
  Tooltip,
  Portal,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

/**
 * DataUiDebugger - Development tool to visualize data-ui tags
 *
 * Usage: Add <DataUiDebugger /> to your Layout component
 * Toggle: Press Ctrl+Shift+U (or Cmd+Shift+U on Mac)
 */
const DataUiDebugger = () => {
  const instanceIdRef = useRef(
    `dataUiDebugger-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  const [enabled, setEnabled] = useState(false);
  const [elements, setElements] = useState([]);
  const [selectedDataUi, setSelectedDataUi] = useState(null);
  const [showList, setShowList] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showOverlays, setShowOverlays] = useState(true);
  const [isPrimaryInstance, setIsPrimaryInstance] = useState(() => {
    // Prevent duplicate overlays when multiple layouts mount the debugger
    if (typeof window === "undefined") return true;
    const owner = window.__dataUiDebuggerOwner;
    if (!owner) {
      window.__dataUiDebuggerOwner = instanceIdRef.current;
      return true;
    }
    return owner === instanceIdRef.current;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const owner = window.__dataUiDebuggerOwner;
    if (!owner) {
      window.__dataUiDebuggerOwner = instanceIdRef.current;
      setIsPrimaryInstance(true);
    } else if (owner === instanceIdRef.current && !isPrimaryInstance) {
      setIsPrimaryInstance(true);
    }

    return () => {
      if (
        isPrimaryInstance &&
        window.__dataUiDebuggerOwner === instanceIdRef.current
      ) {
        delete window.__dataUiDebuggerOwner;
      }
    };
  }, [isPrimaryInstance]);

  useEffect(() => {
    if (!isPrimaryInstance) return;

    if (!showOverlays) {
      setSelectedDataUi(null);
    }
  }, [showOverlays, isPrimaryInstance]);

  useEffect(() => {
    if (!isPrimaryInstance) return;

    const handleKeyPress = (e) => {
      // Toggle with Ctrl+Shift+U (or Cmd+Shift+U on Mac)
      // Using 'U' for UI - avoids browser conflicts
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === "KeyU") {
        e.preventDefault();
        e.stopPropagation();
        setEnabled((prev) => !prev);
      }
    };

    // Use capture phase to catch the event before other handlers
    window.addEventListener("keydown", handleKeyPress, { capture: true });
    return () =>
      window.removeEventListener("keydown", handleKeyPress, { capture: true });
  }, [isPrimaryInstance]);

  useEffect(() => {
    if (!isPrimaryInstance) return;

    if (!enabled) {
      setElements([]);
      return;
    }

    const findDataUiElements = () => {
      const allElements = document.querySelectorAll("[data-ui]");
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const elementData = Array.from(allElements)
        .map((el) => {
          const rect = el.getBoundingClientRect();
          return {
            dataUi: el.getAttribute("data-ui"),
            rect: {
              top: rect.top + window.scrollY,
              left: rect.left + window.scrollX,
              width: rect.width,
              height: rect.height,
            },
            tagName: el.tagName.toLowerCase(),
            // Store viewport position for filtering
            viewportRect: rect,
          };
        })
        .filter((el) => {
          // Filter: only show elements visible in viewport
          const rect = el.viewportRect;

          // Check if element has size (not hidden with display:none or similar)
          if (rect.width === 0 || rect.height === 0) return false;

          // Check if element is within viewport bounds (with small buffer)
          const buffer = 10; // Small buffer to catch elements at edges
          const isInViewport =
            rect.bottom > -buffer && // Not above viewport
            rect.top < viewportHeight + buffer && // Not below viewport
            rect.right > -buffer && // Not left of viewport
            rect.left < viewportWidth + buffer; // Not right of viewport

          return isInViewport;
        });

      setElements(elementData);
    };

    findDataUiElements();

    // Update on scroll and resize
    const handleUpdate = () => {
      requestAnimationFrame(findDataUiElements);
    };

    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    // Update periodically to catch dynamic changes
    const interval = setInterval(findDataUiElements, 1000);

    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
      clearInterval(interval);
    };
  }, [enabled, isPrimaryInstance]);

  if (!isPrimaryInstance) return null;

  if (!enabled) return null;

  // Find duplicates for debugging
  const duplicates = elements.reduce((acc, el) => {
    acc[el.dataUi] = (acc[el.dataUi] || 0) + 1;
    return acc;
  }, {});
  const duplicateDataUis = Object.entries(duplicates)
    .filter(([_, count]) => count > 1)
    .map(([dataUi, count]) => `${dataUi} (${count}x)`);

  if (duplicateDataUis.length > 0) {
    console.warn("⚠️ DUPLICATE data-ui tags found:", duplicateDataUis);
  }

  return (
    <>
      {/* Compact indicator badge and list */}
      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 10000,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        {/* Element list panel */}
        {showList &&
          (() => {
            // Remove duplicates and group by category
            const uniqueElements = [];
            const seenDataUi = new Set();

            elements.forEach((el) => {
              if (!seenDataUi.has(el.dataUi)) {
                seenDataUi.add(el.dataUi);
                uniqueElements.push(el);
              }
            });

            // Build hierarchical structure based on DOM containment
            const buildHierarchy = (elements) => {
              // First, get all DOM elements and build parent-child relationships
              const elementMap = new Map();

              elements.forEach((el) => {
                const domElement = document.querySelector(
                  `[data-ui="${el.dataUi}"]`,
                );
                if (domElement) {
                  elementMap.set(el.dataUi, {
                    ...el,
                    domElement,
                    children: [],
                  });
                }
              });

              // Build parent-child relationships based on DOM containment
              const rootNodes = [];
              const processedNodes = new Set();

              elementMap.forEach((elementData, dataUi) => {
                if (processedNodes.has(dataUi)) return;

                // Find parent by traversing up the DOM
                let parentElement = elementData.domElement.parentElement;
                let parentDataUi = null;

                while (parentElement && !parentDataUi) {
                  const parentDataUiAttr =
                    parentElement.getAttribute("data-ui");
                  if (parentDataUiAttr && elementMap.has(parentDataUiAttr)) {
                    parentDataUi = parentDataUiAttr;
                    break;
                  }
                  parentElement = parentElement.parentElement;
                }

                if (parentDataUi) {
                  // Add as child to parent
                  const parent = elementMap.get(parentDataUi);
                  parent.children.push(elementData);
                } else {
                  // No parent found - this is a root node
                  rootNodes.push(elementData);
                }

                processedNodes.add(dataUi);
              });

              // Convert to hierarchy object format
              const buildNode = (elementData) => {
                const node = {
                  _items: [elementData],
                  _fullDataUi: elementData.dataUi,
                  _label: elementData.dataUi, // Use full data-ui as label
                };

                if (elementData.children.length > 0) {
                  // Has children - add them to the node
                  elementData.children.forEach((child) => {
                    const childKey = child.dataUi; // Use full data-ui as unique key
                    node[childKey] = buildNode(child);
                  });
                }

                return node;
              };

              const hierarchy = {};
              rootNodes.forEach((root) => {
                const rootKey = root.dataUi;
                hierarchy[rootKey] = buildNode(root);
              });

              return hierarchy;
            };

            // Filter elements by search query first
            const filteredElements = uniqueElements.filter((el) =>
              el.dataUi.toLowerCase().includes(searchQuery.toLowerCase()),
            );

            const hierarchy = buildHierarchy(filteredElements);

            // Auto-expand categories on search
            const shouldAutoExpand = searchQuery.length > 0;

            // Recursive component to render hierarchy
            const HierarchyNode = ({
              nodeKey,
              node,
              depth = 0,
              parentPath = "",
            }) => {
              const currentPath = parentPath
                ? `${parentPath}/${nodeKey}`
                : nodeKey;
              const isExpanded =
                shouldAutoExpand || expandedCategories[currentPath];

              // Get child keys (exclude _items, _fullDataUi, _label and other metadata)
              const childKeys = Object.keys(node)
                .filter((key) => !key.startsWith("_"))
                .sort();
              const items = node._items || [];
              const hasChildren = childKeys.length > 0;
              const nodeDataUi = node._fullDataUi || items[0]?.dataUi;
              const displayLabel = node._label || nodeKey;

              const totalCount =
                items.length +
                childKeys.reduce((sum, key) => {
                  const countItems = (n) => {
                    const itemCount = (n._items || []).length;
                    const childCount = Object.keys(n)
                      .filter((k) => !k.startsWith("_"))
                      .reduce((s, k) => s + countItems(n[k]), 0);
                    return itemCount + childCount;
                  };
                  return sum + countItems(node[key]);
                }, 0);

              return (
                <Box key={currentPath}>
                  {/* Category/Item header */}
                  <Box
                    onClick={(e) => {
                      // Check if clicking on the expand/collapse icon
                      if (e.target.closest(".expand-icon")) {
                        setExpandedCategories((prev) => ({
                          ...prev,
                          [currentPath]: !prev[currentPath],
                        }));
                        return;
                      }

                      // Always allow selecting nodes that have data-ui
                      if (nodeDataUi) {
                        e.stopPropagation();
                        setSelectedDataUi(nodeDataUi);
                        const targetElements = document.querySelectorAll(
                          `[data-ui="${nodeDataUi}"]`,
                        );
                        if (targetElements[0]) {
                          targetElements[0].scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      } else if (hasChildren) {
                        // If no data-ui, toggle expansion
                        setExpandedCategories((prev) => ({
                          ...prev,
                          [currentPath]: !prev[currentPath],
                        }));
                      }
                    }}
                    sx={{
                      p: 1,
                      pl: 1 + depth * 1.5,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor:
                        selectedDataUi === nodeDataUi
                          ? "rgba(255, 64, 129, 0.2)"
                          : depth === 0
                            ? "rgba(255, 64, 129, 0.1)"
                            : "rgba(255, 64, 129, 0.05)",
                      borderBottom: "1px solid rgba(255, 64, 129, 0.2)",
                      border:
                        selectedDataUi === nodeDataUi
                          ? "1px solid #ff4081"
                          : "none",
                      borderRadius: selectedDataUi === nodeDataUi ? "4px" : 0,
                      mb: selectedDataUi === nodeDataUi ? 0.5 : 0,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor:
                          selectedDataUi === nodeDataUi
                            ? "rgba(255, 64, 129, 0.25)"
                            : depth === 0
                              ? "rgba(255, 64, 129, 0.15)"
                              : "rgba(255, 64, 129, 0.1)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: hasChildren ? "bold" : "normal",
                          color:
                            selectedDataUi === nodeDataUi
                              ? "#ff4081"
                              : "rgba(255, 255, 255, 0.9)",
                          fontSize: "11px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {displayLabel}
                      </Typography>
                      {hasChildren && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "9px",
                            color: "rgba(255, 255, 255, 0.5)",
                            flexShrink: 0,
                          }}
                        >
                          {totalCount} item{totalCount !== 1 ? "s" : ""}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {nodeDataUi && (
                        <Tooltip title="Copy data-ui tag" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(nodeDataUi);
                            }}
                            sx={{
                              p: 0.5,
                              color: "rgba(255, 255, 255, 0.7)",
                              "&:hover": {
                                color: "#ff4081",
                                backgroundColor: "rgba(255, 64, 129, 0.1)",
                              },
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {hasChildren && (
                        <IconButton
                          size="small"
                          className="expand-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCategories((prev) => ({
                              ...prev,
                              [currentPath]: !prev[currentPath],
                            }));
                          }}
                          sx={{ p: 0, color: "#ff4081" }}
                        >
                          {isExpanded ? (
                            <ExpandLessIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Child categories */}
                  {hasChildren && (
                    <Collapse in={isExpanded}>
                      <Box>
                        {childKeys.map((key) => (
                          <HierarchyNode
                            key={`${currentPath}-${key}`}
                            nodeKey={key}
                            node={node[key]}
                            depth={depth + 1}
                            parentPath={currentPath}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            };

            return (
              <Box
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.95)",
                  border: "2px solid rgba(255, 64, 129, 0.6)",
                  borderRadius: "8px",
                  maxHeight: "500px",
                  minWidth: "300px",
                  maxWidth: "400px",
                  overflow: "hidden",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    p: 1.5,
                    borderBottom: "1px solid rgba(255, 64, 129, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: "bold",
                        color: "#ff4081",
                        fontSize: "11px",
                      }}
                    >
                      Visible UI Tags ({uniqueElements.length} unique)
                    </Typography>
                    {duplicateDataUis.length > 0 && (
                      <Tooltip
                        title={`${duplicateDataUis.length} duplicate data-ui values found! Check console for details.`}
                        arrow
                      >
                        <Chip
                          label={`${duplicateDataUis.length} dupes`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "9px",
                            backgroundColor: "rgba(255, 152, 0, 0.2)",
                            color: "#ffa726",
                            border: "1px solid #ffa726",
                            "& .MuiChip-label": {
                              px: 0.5,
                            },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip
                      title={showOverlays ? "Hide overlays" : "Show overlays"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={() => {
                          setShowOverlays((prev) => !prev);
                          if (showOverlays) {
                            // Clear selection when hiding overlays
                            setSelectedDataUi(null);
                          }
                        }}
                        sx={{
                          p: 0.5,
                          color: showOverlays
                            ? "#ff4081"
                            : "rgba(255, 255, 255, 0.7)",
                          "&:hover": {
                            color: "#ff4081",
                            backgroundColor: "rgba(255, 64, 129, 0.1)",
                          },
                        }}
                      >
                        {showOverlays ? (
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <VisibilityOffIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    {selectedDataUi && (
                      <Tooltip title="Deselect" arrow>
                        <IconButton
                          size="small"
                          onClick={() => setSelectedDataUi(null)}
                          sx={{
                            p: 0.5,
                            color: "rgba(255, 255, 255, 0.7)",
                            "&:hover": {
                              color: "#ff4081",
                              backgroundColor: "rgba(255, 64, 129, 0.1)",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* Search */}
                <Box
                  sx={{
                    p: 1,
                    borderBottom: "1px solid rgba(255, 64, 129, 0.2)",
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoComplete="off"
                    InputProps={{
                      startAdornment: (
                        <SearchIcon
                          sx={{
                            fontSize: 16,
                            mr: 0.5,
                            color: "rgba(255,255,255,0.5)",
                          }}
                        />
                      ),
                      endAdornment: searchQuery && (
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                          sx={{ p: 0, color: "rgba(255,255,255,0.5)" }}
                        >
                          <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      ),
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        fontSize: "12px",
                        fontFamily: "monospace",
                        color: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        "& fieldset": {
                          borderColor: "rgba(255, 64, 129, 0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(255, 64, 129, 0.5)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#ff4081",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "6px 8px",
                      },
                    }}
                  />
                </Box>

                {/* Hierarchical list */}
                <Box sx={{ overflow: "auto", flex: 1 }}>
                  {Object.keys(hierarchy)
                    .sort()
                    .map((key) => (
                      <HierarchyNode
                        key={key}
                        nodeKey={key}
                        node={hierarchy[key]}
                        depth={0}
                        parentPath=""
                      />
                    ))}
                </Box>
              </Box>
            );
          })()}

        {/* Clickable badge */}
        <Chip
          label={(() => {
            const uniqueCount = new Set(elements.map((el) => el.dataUi)).size;
            return `${uniqueCount} tags`;
          })()}
          size="small"
          onClick={() => setShowList(!showList)}
          sx={{
            fontFamily: "monospace",
            fontSize: "10px",
            fontWeight: "bold",
            height: "20px",
            backgroundColor: showList
              ? "rgba(255, 64, 129, 1)"
              : "rgba(255, 64, 129, 0.9)",
            color: "white",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255, 64, 129, 1)",
              transform: "scale(1.05)",
            },
            "& .MuiChip-label": {
              padding: "0 6px",
            },
          }}
        />
      </Box>

      {/* Overlays for each data-ui element */}
      <Portal>
        {showOverlays &&
          elements.map((el, index) => {
            const isSelected = selectedDataUi === el.dataUi;

            // Hide all overlays except selected one when something is selected
            if (selectedDataUi !== null && !isSelected) {
              return null;
            }

            // Calculate z-index based on element size (smaller = higher z-index)
            const elementArea = el.rect.width * el.rect.height;
            // Use inverse of area for z-index (smaller elements on top)
            // Base z-index 9000, subtract a fraction of area to ensure smaller = higher
            const baseZIndex =
              9500 - Math.min(Math.floor(elementArea / 100), 400);
            const zIndex = isSelected ? baseZIndex + 1000 : baseZIndex;

            return (
              <Box
                key={`${el.dataUi}-${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Toggle selection - click to deselect if already selected
                  setSelectedDataUi(isSelected ? null : el.dataUi);
                }}
                sx={{
                  position: "absolute",
                  top: el.rect.top,
                  left: el.rect.left,
                  width: el.rect.width,
                  height: el.rect.height,
                  border: isSelected
                    ? "2px solid #ff4081"
                    : "1px dashed rgba(255, 64, 129, 0.3)",
                  backgroundColor: isSelected
                    ? "rgba(255, 64, 129, 0.15)"
                    : "rgba(255, 64, 129, 0.05)",
                  pointerEvents: "auto",
                  zIndex: zIndex,
                  boxSizing: "border-box",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255, 64, 129, 0.1)",
                    border: "1px solid rgba(255, 64, 129, 0.6)",
                  },
                }}
              >
                {/* Corner indicator dot - always visible when not selected */}
                {!isSelected && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -4,
                      left: -4,
                      width: 8,
                      height: 8,
                      backgroundColor: "#ff4081",
                      borderRadius: "50%",
                      border: "1px solid white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                  />
                )}

                {/* Label - only when selected */}
                {isSelected &&
                  (() => {
                    // Smart positioning to keep label in viewport
                    // Use viewportRect for accurate viewport position
                    const vRect = el.viewportRect;
                    const viewportHeight = window.innerHeight;
                    const viewportWidth = window.innerWidth;
                    const labelHeight = 32;
                    const labelWidth = el.dataUi.length * 8 + 24; // Approximate width

                    // Determine vertical position using viewport coordinates
                    let verticalStyle = {};
                    if (vRect.top > labelHeight + 10) {
                      // Space above - position above
                      verticalStyle = { bottom: "100%", marginBottom: "4px" };
                    } else if (
                      vRect.bottom + labelHeight + 10 <
                      viewportHeight
                    ) {
                      // Space below - position below
                      verticalStyle = { top: "100%", marginTop: "4px" };
                    } else {
                      // Not enough space above or below - position inside at top
                      verticalStyle = { top: "4px" };
                    }

                    // Determine horizontal position using viewport coordinates
                    let horizontalStyle = {};
                    if (vRect.left + labelWidth < viewportWidth - 20) {
                      // Space on right - align left
                      horizontalStyle = { left: 0 };
                    } else if (vRect.right > labelWidth + 20) {
                      // Space on left - align right
                      horizontalStyle = { right: 0 };
                    } else {
                      // Center it
                      horizontalStyle = {
                        left: "50%",
                        transform: "translateX(-50%)",
                      };
                    }

                    return (
                      <Box
                        sx={{
                          position: "absolute",
                          ...verticalStyle,
                          ...horizontalStyle,
                          backgroundColor: "#ff4081",
                          color: "white",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontFamily: "monospace",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.6)",
                          border: "2px solid rgba(255,255,255,0.5)",
                          backdropFilter: "blur(10px)",
                          zIndex: 10001,
                          animation: "fadeIn 0.2s ease",
                          "@keyframes fadeIn": {
                            from: { opacity: 0, transform: "translateY(-4px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                          },
                        }}
                      >
                        {el.dataUi}
                      </Box>
                    );
                  })()}
              </Box>
            );
          })}
      </Portal>
    </>
  );
};

export default DataUiDebugger;
